import useFileSaver from '@hooks/useFileSaver';
import useRequest, { IDataRequest } from '@hooks/useRequest';
import { ICliente } from '@model/Cliente';
import { IClienteEvento } from '@model/ClienteEvento';
import { EnumClienteEventoStatus } from '@model/ClienteEventoStatus';
import { EnumClienteEventoTipo } from '@model/ClienteEventoTipo';
import { IClienteSaldo } from '@model/ClienteSaldo';
import { EnumClienteStatus, IClienteStatus } from '@model/ClienteStatus';
import { IClienteTipo } from '@model/ClienteTipo';
import { IContratoCliente } from '@model/ContratoCliente';
import { EnumContratoClienteStatus } from '@model/ContratoClienteStatus';
import { EnumEmpresa } from '@model/Empresa';
import IFatura, { FaturaTipo } from '@model/Fatura';
import { IFaturaItem } from '@model/FaturaItem';
import { EnumFaturaStatus } from '@model/FaturaStatus';
import { IRemetenteWhatsapp } from '@model/RemetenteWhatsapp';
import { toIdentifier } from '@utils/Functions';
import React from 'react';


export interface IClienteFilter {
    clienteId?: number
    nome?: string
    cpf?: string
    whatsappEnvio?: string
    clienteStatus?: EnumClienteStatus
}

export interface IFaturaFilter {
    faturaStatus: EnumFaturaStatus
}

export interface IClienteEventoFilter {
    clienteEventoTipo: EnumClienteEventoTipo
    clienteEventoStatus: EnumClienteEventoStatus
}

export interface IContratoClienteFilter {
    contratoClienteStatus: EnumContratoClienteStatus
}

export enum EnumTipoSaldo {
    Credito = 1,
    Debito = 2
}

export interface IGeracaoFaturaAntecipadaRequest {
    empresaId: EnumEmpresa
    primeiraCompetencia: string
    numeroFaturas: number
    descricaoRemessa: string
}

export interface IRegistrarFaturaAvulsaRequest extends IFatura {
    quantidade: number
    descricaoRemessa: string
    faturaTipo: FaturaTipo
    faturaItens: IFaturaItem[]
}

const controller = "Cliente"

export function useClienteService () {
    const _request = useRequest();
    const fileSaver = useFileSaver();

    const carregar = React.useCallback((clienteId: number) => {
        return _request.get<ICliente>(`${controller}/Carregar/${clienteId}`);
    }, [_request]);

    const listarFaturas = React.useCallback((clienteId: number, filter: IFaturaFilter) => {
        return _request.get<IFatura[]>(`${controller}/ListarFaturas/${clienteId}`, {
            params: filter
        })
    }, [_request]);

    const listarEventos = React.useCallback((clienteId: number, filter: IClienteEventoFilter) => {
        return _request.get<IClienteEvento[]>(`${controller}/ListarEventos/${clienteId}`, {
            params: filter
        })
    }, [_request]);

    const listarSaldos = React.useCallback((clienteId: number) => {
        return _request.get<IClienteSaldo[]>(`${controller}/ListarSaldos/${clienteId}`)
    }, [_request]);

    const listarContratos = React.useCallback((clienteId: number, filter: IContratoClienteFilter) => {
        return _request.get<IContratoCliente[]>(`${controller}/ListarContratos/${clienteId}`, {
            params: filter
        })
    }, [_request]);

    const listarContratosFaturaManual = React.useCallback((clienteId: number, empresaId: EnumEmpresa) => {
        return _request.get<IContratoCliente[]>(`${controller}/ListarContratosFaturaManual/${clienteId}/${empresaId}`)
    }, [_request]);

    const listar = React.useCallback((request: IDataRequest) => {
        return _request.getData<ICliente[]>(`${controller}/Listar`, request);
    }, [_request]);

    const listarTiposAtivos = React.useCallback(() => {
        return _request.get<IClienteTipo[]>(`${controller}/ListarTiposAtivos`);
    }, [_request]);

    const listarStatusAtivos = React.useCallback(() => {
        return _request.get<IClienteStatus[]>(`${controller}/ListarStatusAtivos`);
    }, [_request]);

    const listarRemetentesWhatsappAtivos = React.useCallback(() => {
        return _request.get<IRemetenteWhatsapp[]>(`${controller}/ListarRemetentesWhatsappAtivos`);
    }, [_request]);


    const editar = React.useCallback((clienteId: number, cliente: ICliente) => {
        return _request.post<ICliente>(`${controller}/Editar/${clienteId}`, cliente);
    }, [_request]);

    const registrarEvento = React.useCallback((clienteId: number, clienteEvento: IClienteEvento) => {
        return _request.post<IClienteEvento>(`${controller}/RegistrarEvento/${clienteId}`, clienteEvento);
    }, [_request]);

    const registrarFaturaManual = React.useCallback((clienteId: number, request: IRegistrarFaturaAvulsaRequest) => {
        return _request.downloadPost(`${controller}/RegistrarFaturaManual/${clienteId}`, request)
            .then(response => { 
                fileSaver.save(response, `${toIdentifier(request.descricaoRemessa)}.rem`);
            });
    }, [_request, fileSaver]);

    const registrarSaldo = React.useCallback((clienteId: number, tipoSaldo: EnumTipoSaldo, clienteSaldo: IClienteSaldo) => {
        return _request.post<IClienteSaldo>(`${controller}/RegistrarSaldo/${clienteId}/${tipoSaldo}`, clienteSaldo);
    }, [_request]);

    return React.useMemo(() => ({
        registrarFaturaManual, listarContratosFaturaManual, carregar, listar, listarTiposAtivos, listarStatusAtivos, listarRemetentesWhatsappAtivos,
        editar, registrarEvento,
        listarFaturas, listarEventos,listarContratos,listarSaldos, registrarSaldo
    }), [registrarFaturaManual, listarContratosFaturaManual, carregar, listar, listarTiposAtivos, listarStatusAtivos, listarRemetentesWhatsappAtivos, editar, registrarEvento, listarFaturas, listarEventos, listarContratos, listarSaldos, registrarSaldo]);
}